import { IconButton, Typography } from '@volvo/vcdk-react';
import { useTranslation } from 'react-i18next';

import type { IFuelConsumptionResponse } from '@/service/vpc/types/IChassisFuelConsumption';
import type { IUnit } from '@/service/vpc/types/IUnit';
import type { ColumnDef } from '@tanstack/react-table';

import useMediaQuery from '@/hooks/useMediaQuery';
import { chassis } from '@/utils';

export type IFuelConsumptionTableRow = {
  unitName: string;
  chassisId: string;
  position: string | null;
  opHours: number | null;
  fuelEfficiency: number | null;
  totalFuel: number | null;
  subRows?: IFuelConsumptionTableRow[] | null;
};

export const mapFuelConsumptionTableData = (
  data: IFuelConsumptionResponse | undefined,
  units: IUnit[] | undefined
): IFuelConsumptionTableRow[] => {
  if (!data) return [];
  const chassisToUnit = new Map(
    (units || []).flatMap((unit) => chassis.getUnitsChassisIds(unit).map((id) => [id, unit]))
  );

  // First, process all chassis data
  const chassisDataMap = new Map<string, IFuelConsumptionTableRow[]>();

  Object.entries(data).forEach(([chassisId, consumptions]) => {
    const unit = chassisToUnit.get(chassisId);
    const unitName = unit?.unitName || chassisId;
    const position = chassis.getDrivelinePosition(unit!, chassisId).positionName;
    let opHours = 0;
    let totalFuel = 0;

    consumptions.forEach((consumption) => {
      if (consumption.HourDifference) {
        opHours += consumption.HourDifference;
      }
      if (consumption.LiterDifference) {
        totalFuel += consumption.LiterDifference;
      }
    });

    const fuelEfficiency = opHours > 0 ? totalFuel / opHours : 0;

    const chassisRow: IFuelConsumptionTableRow = {
      unitName,
      chassisId,
      position,
      opHours: Number(opHours.toFixed(2)),
      fuelEfficiency: Number(fuelEfficiency.toFixed(2)),
      totalFuel: Number(totalFuel.toFixed(2)),
      subRows: null, // Initialize subRows as an empty array
    };

    // Group chassis by unit name
    if (!chassisDataMap.has(unitName)) {
      chassisDataMap.set(unitName, []);
    }
    chassisDataMap.get(unitName)!.push(chassisRow);
  });

  // Now create parent rows with aggregated data and subRows for individual chassis
  const tableData: IFuelConsumptionTableRow[] = [];

  chassisDataMap.forEach((chassisRows, unitName) => {
    if (chassisRows.length === 1) {
      // If unit has only one chassis, add it as a regular row without subRows
      tableData.push(chassisRows[0]);
    } else {
      // If unit has multiple chassis, create parent row with aggregated data
      const totalOpHours = chassisRows.reduce((sum, row) => sum + (row.opHours || 0), 0);
      const totalFuelSum = chassisRows.reduce((sum, row) => sum + (row.totalFuel || 0), 0);
      const aggregatedFuelEfficiency = totalOpHours > 0 ? totalFuelSum / totalOpHours : 0;

      const parentRow: IFuelConsumptionTableRow = {
        unitName,
        chassisId: `${chassisRows.length} chassis`, // Display count of chassis
        position: null, // Parent row doesn't have a position
        opHours: null, // Parent row doesn't have opHours
        fuelEfficiency: Number(aggregatedFuelEfficiency.toFixed(2)),
        totalFuel: Number(totalFuelSum.toFixed(2)),
        subRows: chassisRows,
      };

      tableData.push(parentRow);
    }
  });

  return tableData;
};

const useFuelConsumptionTable = () => {
  const { t } = useTranslation();
  const { mediaQueryNodes: mq } = useMediaQuery();

  const columns = [
    {
      accessorKey: 'expand',
      id: 'expand',
      header: '',
      maxSize: 64,
      enableSorting: false,
      enableColumnFilter: false,
      meta: {
        order: 5,
      },
      cell: (info) => {
        const hasSubRows = info.row.original.subRows && info.row.original.subRows.length > 0;
        if (!hasSubRows) return null;

        return (
          <div>
            <IconButton
              icon={info.row.getIsExpanded() ? 'chevron-up' : 'chevron-down'}
              onClick={info.row.getToggleExpandedHandler()}
            />
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.unitName,
      id: 'unitName',
      header: t(($) => $.components.table.fuel_consumption_table.unit),
      sortingFn: 'alphanumeric',
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex flex-col">
            <Typography
              variant={mq({
                default: 'heading4',
                md: 'caption1',
              })}
            >
              {info.getValue<string>()}
            </Typography>
            <Typography variant="caption2">{row.position}</Typography>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.chassisId,
      id: 'chassisId',
      header: t(($) => $.components.table.fuel_consumption_table.chassis_id),
      cell: (info) => {
        return <Typography variant="caption1">{info.getValue<string>()}</Typography>;
      },
    },
    {
      accessorFn: (row) => row.opHours,
      id: 'opHours',
      header: t(($) => $.components.table.fuel_consumption_table.operating_hours),
      sortingFn: (a, b) => (a.getValue<number>('opHours') || 0) - (b.getValue<number>('opHours') || 0),
      cell: (info) => {
        const opHours = info.getValue<number>();
        return (
          <Typography variant="caption1">{opHours !== null ? opHours.toFixed(1).toLocaleString() : '-'}</Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.fuelEfficiency,
      id: 'fuelEfficiency',
      header: t(($) => $.components.table.fuel_consumption_table.fuel_efficiency),
      sortingFn: (a, b) => (a.getValue<number>('fuelEfficiency') || 0) - (b.getValue<number>('fuelEfficiency') || 0),
      cell: (info) => {
        const efficiency = info.getValue<number>();
        return (
          <Typography variant="caption1">
            {efficiency !== null ? efficiency.toFixed(1).toLocaleString() : '-'}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.totalFuel,
      id: 'totalFuel',
      header: t(($) => $.components.table.fuel_consumption_table.total_fuel),
      sortingFn: (a, b) => (a.getValue<number>('totalFuel') || 0) - (b.getValue<number>('totalFuel') || 0),
      cell: (info) => {
        const totalFuel = info.getValue<number>();
        return (
          <Typography variant="caption1">{totalFuel !== null ? totalFuel.toFixed(1).toLocaleString() : '-'}</Typography>
        );
      },
    },
  ] as ColumnDef<IFuelConsumptionTableRow>[];

  return { columns };
};

export default useFuelConsumptionTable;
